import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';

import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { useCommunicationChannelData } from '@/pages/communicationChannel/communicationChannel.queries';
import { useAllContactData } from '@/pages/contact/contact.queries';
import { restUrl } from '@/services/utils';

import {
    applyCustomPublicationTargetFilter,
    expandAndFilterChannels,
    filterOrganizations,
} from '../lib/publicationDestinations';
import type {
    ChannelDestination,
    ChannelTypeVerification,
    OrganizationDestination,
    PublicationMode,
} from '../types';
import {
    getPublicationAngularContext,
    getPublicationPlanSignature,
    hasPublicationTargetFilter,
} from './publicationAngularBridge';

const useChannelTypeVerification = () => {
    return useQuery<ChannelTypeVerification>({
        queryKey: ['communicationchannelTypeVerification'],
        queryFn: async () => {
            const response = await axios.get<ChannelTypeVerification>(
                restUrl('communicationchannelTypeVerification'),
            );
            return response.data ?? {};
        },
    });
};

const usePublicationDestinations = (mode: PublicationMode) => {
    const { organization } = useOrganization();
    const { user } = useUser();
    const contactsQuery = useAllContactData(true);
    const channelsQuery = useCommunicationChannelData(true);
    const verificationQuery = useChannelTypeVerification();
    const planSignature = getPublicationPlanSignature();
    const publicationTargetFilterReady = hasPublicationTargetFilter();

    // planSignature and publicationTargetFilterReady only invalidate the memo below.
    const destinations = useMemo(() => {
        const context = getPublicationAngularContext();
        if (!contactsQuery.data || !channelsQuery.data || !verificationQuery.data) {
            return {
                organizations: [] as OrganizationDestination[],
                communicationChannels: [] as ChannelDestination[],
                plans: context.plans,
                customPublicationSettings: context.customPublicationSettings,
                contextUser: context.user,
                contextOrganization: context.organization,
            };
        }

        const organizations = filterOrganizations(
            contactsQuery.data as OrganizationDestination[],
            organization?.id,
        );
        const communicationChannels = expandAndFilterChannels({
            channels: channelsQuery.data as ChannelDestination[],
            plans: context.plans,
            verification: verificationQuery.data,
            mode,
            translateFormat: context.translateFormat,
        });
        const filtered = applyCustomPublicationTargetFilter({
            organizations,
            communicationChannels,
            mode,
            customPublicationSettings: context.customPublicationSettings,
            user: user ?? context.user,
            organization: organization ?? context.organization,
        });

        return {
            ...filtered,
            plans: context.plans,
            customPublicationSettings: context.customPublicationSettings,
            contextUser: context.user,
            contextOrganization: context.organization,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        channelsQuery.data,
        contactsQuery.data,
        mode,
        organization,
        planSignature,
        publicationTargetFilterReady,
        user,
        verificationQuery.data,
    ]);

    const isLoading = contactsQuery.isPending
        || channelsQuery.isPending
        || verificationQuery.isPending;

    return {
        organizations: destinations.organizations,
        communicationChannels: destinations.communicationChannels,
        isLoading,
        plans: destinations.plans,
        customPublicationSettings: destinations.customPublicationSettings,
        user: user ?? destinations.contextUser,
        organization: organization ?? destinations.contextOrganization,
    };
};

export { useChannelTypeVerification, usePublicationDestinations };
